import { useEffect, useState, useRef } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { VideoCard } from "@/components/VideoCard";

export function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    if (!open) { setQ(""); setItems([]); }
  }, [open]);

  useEffect(() => {
    if (!open || !q.trim()) { setItems([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search", { params: { q: q.trim(), limit: 20 } });
        setItems(data.items || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [q, open]);

  if (!open) return null;

  return (
    <div
      data-testid="search-overlay"
      className="fixed inset-0 z-[60] bg-bg/95 backdrop-blur-2xl overflow-y-auto"
    >
      <div className="mx-auto w-full max-w-md min-h-screen pb-24">
        <div className="sticky top-0 bg-bg/90 backdrop-blur-xl border-b border-white/5 px-4 py-3 flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 bg-bg-surface border border-white/10 rounded-full px-4 py-2.5">
            <Search size={18} className="text-white/60" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="search-input"
              placeholder="Search videos, creators..."
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/40"
            />
            {loading && <Loader2 size={16} className="animate-spin text-brand" />}
          </div>
          <button
            onClick={onClose}
            data-testid="search-close"
            className="w-10 h-10 rounded-full grid place-items-center text-white/75 hover:bg-white/5"
            aria-label="Close search"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-4 py-4">
          {!q.trim() && (
            <div className="text-center py-16 text-white/40 text-sm">
              <p className="font-heading text-white/70 text-lg mb-1">Start typing</p>
              <p>Try "bangla natok", "dhaka vlog", "BD music"…</p>
            </div>
          )}
          {q.trim() && !loading && items.length === 0 && (
            <p className="text-white/40 text-sm text-center py-12">No results.</p>
          )}
          <div className="grid grid-cols-1 gap-5 mt-2">
            {items.map((v) => (
              <VideoCard key={v.video_id} video={v} variant="full" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
